import { logClientAuditEvent } from '../../project/services/auditTrailApi';
import { ensureAuditSession } from '../../audit/auditSession';

const PIPEFITTER_MODULE = 'PIPEFITTER';

export function logPipeIdRenamed(params: {
  projectId?: number | null;
  projectName?: string | null;
  projectNumber?: string | null;
  statusId?: number | string | null;
  beforePipeId: string;
  afterPipeId: string;
  endChanges?: Array<{
    end: 'EndA' | 'EndB';
    beforePipeEndId: string;
    afterPipeEndId: string;
    beforeFileUrl?: string | null;
    afterFileUrl?: string | null;
  }>;
  s3Updates?: Array<{
    end: 'EndA' | 'EndB';
    oldPipeEndID: string;
    newPipeEndID: string;
    oldFileUrl?: string | null;
    newFileUrl?: string | null;
    skipped?: boolean;
    error?: string;
  }>;
}): void {
  if (!params.beforePipeId || params.beforePipeId === params.afterPipeId) {
    const hasEndChanges = (params.endChanges?.length ?? 0) > 0;
    if (!hasEndChanges) return;
  }
  ensureAuditSession();
  const endSummary = (params.endChanges ?? [])
    .map((change) => `${change.end}: ${change.beforePipeEndId} → ${change.afterPipeEndId}`)
    .join('; ');
  logClientAuditEvent({
    eventType: 'PIPEFITTER_PIPE_ID_RENAMED',
    module: PIPEFITTER_MODULE,
    entityType: 'PIPE',
    entityId: params.afterPipeId || params.beforePipeId,
    entityName: params.afterPipeId || params.beforePipeId,
    projectId: params.projectId ?? undefined,
    projectName: params.projectName ?? undefined,
    summary: endSummary
      ? `Renamed pipe ID ${params.beforePipeId} → ${params.afterPipeId} (${endSummary}).`
      : `Renamed pipe ID ${params.beforePipeId} → ${params.afterPipeId}.`,
    displayTitle: params.afterPipeId || params.beforePipeId,
    actionDetails: {
      before: { pipeID: params.beforePipeId },
      after: { pipeID: params.afterPipeId },
      statusId: params.statusId ?? null,
      projectNumber: params.projectNumber ?? null,
      endChanges: params.endChanges ?? [],
      s3Updates: params.s3Updates ?? [],
    },
  });
}

export function logPipesMovedToPipeSize(params: {
  projectId?: number | null;
  projectName?: string | null;
  projectNumber?: string | null;
  moves: Array<{
    pipeID: string;
    pipeStatusId?: number | string | null;
    /** Group before the move. */
    beforeGroupId: string;
    /** Group after the move. */
    afterGroupId: string;
    beforePipeId?: number | string | null;
    afterPipeId?: number | string | null;
    mergedIntoPipeStatusId?: number | string | null;
    sourceDeleted?: boolean;
    s3?: unknown;
  }>;
}): void {
  if (!params.moves.length) return;
  ensureAuditSession();
  for (const move of params.moves) {
    logClientAuditEvent({
      eventType: 'PIPEFITTER_PIPE_MOVED_TO_PIPE_SIZE',
      module: PIPEFITTER_MODULE,
      entityType: 'PIPE',
      entityId: move.pipeID,
      entityName: move.pipeID,
      projectId: params.projectId ?? undefined,
      projectName: params.projectName ?? undefined,
      summary: move.mergedIntoPipeStatusId
        ? `Moved pipe ${move.pipeID} from group ${move.beforeGroupId || '—'} to group ${move.afterGroupId || '—'} (merged onto status ${move.mergedIntoPipeStatusId}; source row deleted).`
        : `Moved pipe ${move.pipeID} from group ${move.beforeGroupId || '—'} to group ${move.afterGroupId || '—'}.`,
      displayTitle: move.pipeID,
      actionDetails: {
        before: {
          groupID: move.beforeGroupId,
          pipe_id: move.beforePipeId ?? null,
          pipeStatusId: move.pipeStatusId ?? null,
        },
        after: {
          groupID: move.afterGroupId,
          pipe_id: move.afterPipeId ?? null,
          pipeStatusId: move.mergedIntoPipeStatusId ?? move.pipeStatusId ?? null,
        },
        pipeID: move.pipeID,
        projectNumber: params.projectNumber ?? null,
        mergedIntoPipeStatusId: move.mergedIntoPipeStatusId ?? null,
        sourceDeleted: Boolean(move.sourceDeleted),
        s3: move.s3 ?? null,
      },
    });
  }
}

export function logPipeEndsDeleted(params: {
  projectId?: number | null;
  projectName?: string | null;
  projectNumber?: string | null;
  statusId?: number | string | null;
  pipeID: string;
  ends: Array<'EndA' | 'EndB'>;
  softDeletedStatus?: boolean;
}): void {
  if (!params.ends.length) return;
  ensureAuditSession();
  const endsLabel = params.ends.join(' + ');
  logClientAuditEvent({
    eventType: 'PIPEFITTER_PIPE_ENDS_DELETED',
    module: PIPEFITTER_MODULE,
    entityType: 'PIPE',
    entityId: params.pipeID,
    entityName: params.pipeID,
    projectId: params.projectId ?? undefined,
    projectName: params.projectName ?? undefined,
    summary: `Deleted ${endsLabel} for pipe ${params.pipeID}${
      params.softDeletedStatus ? ' (status soft-deleted)' : ''
    }.`,
    displayTitle: params.pipeID,
    actionDetails: {
      pipeID: params.pipeID,
      ends: params.ends,
      softDeletedStatus: Boolean(params.softDeletedStatus),
      statusId: params.statusId ?? null,
      projectNumber: params.projectNumber ?? null,
    },
  });
}
